import React, { useState, useEffect } from 'react';
import { Briefcase, MessageSquare, Compass, Quote, FileText, Image, Users, Globe, Plus, Clock, ChevronDown, ChevronUp, Eye, PhoneCall, UserCheck, TrendingUp, Send, Mail, MapPin, Phone, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuickActionsPanel = ({ token }) => {
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentLeads();
  }, []);

  const fetchRecentLeads = async () => {
    try {
      const response = await axios.get(`${API}/admin/quick-actions/recent-leads?limit=8`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentLeads(response.data);
    } catch (error) {
      console.error('Error fetching recent leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCall = (phone) => {
    window.open(`tel:${phone}`, '_self');
  };

  const handleEmail = (email) => {
    window.open(`mailto:${email}`, '_blank');
  };

  const handleWhatsApp = (phone) => {
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    window.open(`https://wa.me/${cleanPhone}`, '_blank');
  };

  const getStatusColor = (status) => {
    const colors = {
      'completed': '#48bb78',
      'pending_verification': '#ed8936',
      'generating': '#4299e1',
      'callback_requested': '#9f7aea',
      'contacted': '#38b2ac',
      'converted': '#38a169',
      'lost': '#e53e3e'
    };
    return colors[status] || '#718096';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  if (loading) {
    return (
      <div className="quick-actions-panel">
        <h3><Users size={20} /> Recent Leads - Quick Actions</h3>
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="quick-actions-panel" data-testid="quick-actions-panel">
      <div className="quick-actions-header">
        <h3><Users size={20} /> Recent Leads - Quick Actions</h3>
        <Button onClick={fetchRecentLeads} className="refresh-btn-small">
          <RefreshCw size={14} />
        </Button>
      </div>
      
      {recentLeads.length === 0 ? (
        <p className="no-leads-message">No leads yet. They will appear here once customers start using the Design My Journey form.</p>
      ) : (
        <div className="quick-actions-grid">
          {recentLeads.map((lead) => (
            <div key={lead.id} className="quick-action-card" data-testid={`quick-action-${lead.id}`}>
              <div className="quick-action-header">
                <span className="lead-name">{lead.name || 'Unknown'}</span>
                <span 
                  className="lead-status-badge" 
                  style={{ backgroundColor: getStatusColor(lead.status) }}
                >
                  {lead.status?.replace(/_/g, ' ') || 'pending'}
                </span>
              </div>
              
              <div className="quick-action-details">
                <span className="lead-destination">
                  <MapPin size={12} /> {lead.destination || 'Not specified'}
                </span>
                <span className="lead-time">
                  <Clock size={12} /> {formatDate(lead.created_at)}
                </span>
              </div>
              
              <div className="quick-action-buttons">
                <button 
                  className="action-btn call-btn" 
                  onClick={() => handleCall(lead.phone)}
                  title={`Call ${lead.phone}`}
                  disabled={!lead.phone}
                >
                  <Phone size={14} />
                </button>
                <button 
                  className="action-btn email-btn" 
                  onClick={() => handleEmail(lead.email)}
                  title={`Email ${lead.email}`}
                  disabled={!lead.email}
                >
                  <Mail size={14} />
                </button>
                <button 
                  className="action-btn whatsapp-btn" 
                  onClick={() => handleWhatsApp(lead.phone)}
                  title="WhatsApp"
                  disabled={!lead.phone}
                >
                  <MessageSquare size={14} />
                </button>
                {lead.has_pdf && (
                  <span className="pdf-indicator" title="PDF Generated">
                    <FileText size={14} />
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// User Management Panel Component

export { QuickActionsPanel };
